import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import { ModifierGroup } from "../../pos/types/pos.types";

export interface POSSellableItem {
  pos_sellable_item_id: string;
  name: string;
  description?: string;
  category?: string;
  price: number;
  total_cost: number;
  currency: string;
  recipe_id?: string;
  preparation_time: number;
  calories?: number;
  allergens: string[];
  tags: string[];
  image_url?: string;
  is_available: boolean;
  is_active: boolean;
  is_deleted: boolean;
  store_id: string;
  created_at: number;
  created_by: string;
  updated_at: number;
  updated_by: string;
  activity_logs: Array<{
    timestamp: number;
    action: "create" | "update" | "delete" | "status_change";
    performed_by: string;
    changes?: Record<string, any>;
    notes?: string;
    modifiers: ModifierGroup[];
  }>;
  recipes: Array<{
    recipe_id: string;
    name: string;
    quantity: number;
    unit: string;
  }>;
}

interface ListParams {
  searchQuery?: string;
  filters?: Array<{
    id: string;
    title: string;
    field: string;
    operator: string;
    value: any;
  }>;
  startDate?: number;
  endDate?: number;
  dateField?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex: number;
  pageSize: number;
  category?: string;
}

interface ListResponse {
  data: POSSellableItem[];
  metadata: {
    total: {
      value: number;
    };
    took: number;
    timed_out: boolean;
    max_score: number | null;
  };
}

interface CreatePOSSellableItemData {
  name: string;
  description?: string;
  category?: string;
  recipe_id?: string;
  total_cost?: number;
  price: number;
  calories?: number;
  allergens?: string[];
  tags?: string[];
  image_url?: string;
  ingredients: Array<{
    item_id: string;
    ingredient_id: string;
    quantity: number;
    unit: string;
    item_type: string;
  }>;
  processed_items: Array<{
    processed_item_id: string;
    name: string;
    quantity: number;
    unit: string;
  }>;
}

interface UpdatePOSSellableItemData {
  pos_sellable_item_id: string;
  name?: string;
  description?: string;
  category?: string;
  price?: number;
  total_cost?: number;
  preparation_time?: number;
  calories?: number;
  allergens?: string[];
  tags?: string[];
  image_url?: string;
  is_available?: boolean;
  is_active?: boolean;
  recipes?: Array<{
    recipe_id: string;
    quantity: number;
    unit: string;
  }>;
}

class POSSellableItemsService {
  private readonly baseUrl = "/business-admin/sellable-items";

  async listPOSSellableItems(params: ListParams): Promise<any> {
    const response = await axiosInstance.post(`${this.baseUrl}/list`, params);
    return response.data.data;
  }

  async getPOSSellableItem(
    id: string
  ): Promise<AxiosResponse<POSSellableItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/get`, {
      _id: id,
    });
    return response;
  }

  async createPOSSellableItem(
    data: CreatePOSSellableItemData
  ): Promise<AxiosResponse<POSSellableItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/create`, data);
    return response;
  }

  async updatePOSSellableItem(
    data: UpdatePOSSellableItemData
  ): Promise<AxiosResponse<POSSellableItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/update`, data);
    return response;
  }

  async deletePOSSellableItem(_id: string): Promise<AxiosResponse<null>> {
    const response = await axiosInstance.post(`${this.baseUrl}/delete`, {
      _id,
    });
    return response;
  }

  async toggleAvailability(
    id: string,
    isAvailable: boolean
  ): Promise<AxiosResponse<null>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/toggle-availability`,
      {
        pos_sellable_item_id: id,
        is_available: isAvailable,
      }
    );
    return response;
  }
}

export const posSellableItemsService = new POSSellableItemsService();
